import { apiService } from '../../../shared/services/apiServices';
import { AxiosError } from 'axios';
import { SUBSCRIPTION_ERROR_MESSAGES } from '../errors';

// ============================================================================
// API Response Types
// ============================================================================

export type SubscriptionPlanStatus = 'active' | 'inactive';
export type DurationType = 'MONTHLY' | 'YEARLY';
export type BillingCycle = 'MONTHLY' | 'QUARTERLY' | 'YEARLY';

export interface SubscriptionPlanLimits {
  numberOfUsers?: number;
  numberOfFacilities?: number;
  numberOfQmsDocuments?: number;
}

export interface SubscriptionPlanOffer {
  offerId: string;
  offerName: string;
}

export interface SubscriptionPlan {
  id: string;
  planName: string;
  planDescription: string;
  durationType: DurationType;
  duration: number;
  billingCycle: BillingCycle;
  price: number;
  limits: SubscriptionPlanLimits | null;
  offer?: SubscriptionPlanOffer | null;
  offerId?: string | null;
  status: SubscriptionPlanStatus;
  createdAt: string;
  updatedAt: string;
  createdBy:
    | string
    | { name?: string; email?: string; [key: string]: unknown }
    | null;
  updatedBy:
    | string
    | { name?: string; email?: string; [key: string]: unknown }
    | null;
}

export interface ViewSubscriptionPlanApiResponse {
  success: boolean;
  data: SubscriptionPlan;
}

export interface ViewSubscriptionPlanResult {
  success: boolean;
  data?: SubscriptionPlan;
  error?: string;
  forbidden?: boolean;
}

export interface ViewSubscriptionPlanOptions {
  planId: string;
}

// ============================================================================
// Error Messages
// ============================================================================

export const ERROR_MESSAGES = {
  DEFAULT_ERROR: 'Wystąpił błąd podczas pobierania danych planu subskrypcji',
  NETWORK_ERROR: SUBSCRIPTION_ERROR_MESSAGES.NETWORK_ERROR,
  UNEXPECTED_ERROR: SUBSCRIPTION_ERROR_MESSAGES.UNEXPECTED_ERROR,
} as const;

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return ERROR_MESSAGES.DEFAULT_ERROR;
  }

  if (typeof errorData === 'string') {
    return errorData;
  }

  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    const errorMessage = apiError.message || apiError.error;

    if (errorMessage) {
      return errorMessage;
    }

    return ERROR_MESSAGES.DEFAULT_ERROR;
  }

  return ERROR_MESSAGES.DEFAULT_ERROR;
};

/**
 * Fetches subscription plan details by planId (UUID).
 * Endpoint: GET loopay-subscription-management/subscriptions/{planId}
 */
export const viewSubscriptionPlan = async (
  options: ViewSubscriptionPlanOptions
): Promise<ViewSubscriptionPlanResult> => {
  const { planId } = options;

  if (!planId?.trim()) {
    return {
      success: false,
      error: ERROR_MESSAGES.DEFAULT_ERROR,
    };
  }

  try {
    const result = await apiService.getAPI(
      `loopay-subscription-management/subscriptions/${planId.trim()}`,
      { skipGlobalErrorRedirect: true }
    );

    if (result.error && result.status === 403) {
      return { success: false, forbidden: true };
    }

    if (!result.error) {
      // API returns { success: true, data: {...} }
      const response = result.data as ViewSubscriptionPlanApiResponse;
      const planData = (response.data || result.data) as SubscriptionPlan;
      const normalizedOffer =
        planData.offer ??
        (planData.offerId
          ? {
              offerId: planData.offerId,
              offerName: '',
            }
          : null);

      return {
        success: true,
        data: {
          ...planData,
          offer: normalizedOffer,
        },
      };
    } else {
      const errorData = result.data;
      const isNetworkError = result.isNetworkError;

      const errorMessage = extractErrorMessage(errorData);

      if (isNetworkError) {
        return {
          success: false,
          error: ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  } catch (error) {
    if (error instanceof AxiosError) {
      const errorData = error.response?.data;
      const isNetworkError = !error.response && !!error.request;

      if (isNetworkError) {
        return {
          success: false,
          error: ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      const errorMessage = extractErrorMessage(errorData);

      return {
        success: false,
        error: errorMessage,
      };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
